import { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import { Provider } from 'react-redux';
import { setAutoFreeze } from 'immer';
import { DesignSystemProvider, token } from '@bynder/design-system';
import { LocalizationProvider } from '@bynder/localization';
import { createGlobalStyle } from 'styled-components';
import { initSentry } from './initSentry';
import App from 'packages/pages/App';
import store from 'packages/store';
import initServerConnector from 'packages/initServerConnector';
import { checkPlatformUsage } from 'packages/store/platform/platform.actions';
import fetchFallback from 'packages/helpers/localization';
import installPortalFetchRewrite from 'packages/helpers/installPortalFetchRewrite';
import { setHostConfig, type HostConfig } from 'packages/helpers/hostConfig';
import { normalizeBasePath } from 'packages/helpers/navigation';
import { GlobalStyles, STUDIO_ROOT_CLASS } from 'packages/common/globalStyles/GlobalStyles';

import './serviceWorker';
import 'requestidlecallback-polyfill';

export type StudioConfig = HostConfig & {
    /**
     * Absolute origin with a trailing slash, used to build share links and the
     * Figma redirect URL. In the iframe deployment the host sends this over the
     * message channel instead.
     */
    hostUrl?: string;
    /**
     * URL returning `{ "access_token": "..." }`. When set, Studio owns fetching
     * and refreshing the token from here; when omitted it waits for the host's
     * message channel, which is how the iframe deployment works.
     */
    tokenEndpoint?: string;
};

const GlobalVideoStyle = createGlobalStyle`
    .video-js:hover .vjs-big-play-button,
    .video-js .vjs-big-play-button:focus,
    .video-js .vjs-big-play-button:active {
        background-color: ${token.brandPrimary500} !important;
    }

    .video-js .vjs-loading-spinner {
        border-color: ${token.brandPrimary500} !important;
    }

    .video-js .vjs-play-progress,
    .video-js .vjs-volume-level {
        background-color: ${token.brandPrimary500} !important;
    }

    [role="listbox"], [role="menu"] {
        overscroll-behavior: none;
    }
`;

if (Intl?.PluralRules?.prototype?.select) {
    const originalSelect = Intl.PluralRules.prototype.select;

    Intl.PluralRules.prototype.select = function (value) {
        if (value === 0) {
            return 'zero';
        }

        return originalSelect.call(this, value);
    };
}

/**
 * Mounts Studio into `container`.
 *
 * Both entry paths go through here: the `<vbs-console>` custom element and, for
 * as long as it exists, the iframe bootstrap in `bootstrap.ts`.
 */
export function renderApp(container: Element, config: StudioConfig = {}): void {
    const { hostUrl, tokenEndpoint, ...hostConfig } = config;
    const basename = normalizeBasePath(hostConfig.basePath);

    // Must precede initSentry(), which resolves the environment once and holds
    // onto it for the lifetime of the client, and any navigation helper, which
    // reads the base path from here.
    setHostConfig({ ...hostConfig, basePath: basename });

    initSentry();
    store.dispatch(checkPlatformUsage({ hostUrl, tokenEndpoint }));
    initServerConnector();
    // TEMPORARY: no-op unless Studio is on a different origin from the portal,
    // i.e. only on the iframe path. Kept here rather than in the iframe
    // bootstrap so it still wraps Sentry's own fetch instrumentation, which
    // would otherwise report the pre-rewrite relative URL.
    // TODO(iframe-removal): remove this call and delete packages/helpers/installPortalFetchRewrite.ts
    installPortalFetchRewrite();
    setAutoFreeze(false);

    container.classList.add(STUDIO_ROOT_CLASS);

    ReactDOM.createRoot(container).render(
        <DesignSystemProvider>
            <GlobalStyles />
            <GlobalVideoStyle />
            <LocalizationProvider fetchFallback={fetchFallback} project="creative-studio-frontend" forceFallback>
                <Provider key="provider" store={store}>
                    <Router basename={basename}>
                        <Suspense fallback={null}>
                            <Routes>
                                <Route path="*" element={<App />} />
                            </Routes>
                        </Suspense>
                    </Router>
                </Provider>
            </LocalizationProvider>
        </DesignSystemProvider>,
    );
}
